import httpClient from "../../../../../api/httpClient";
import { formatDateYMD, getTodayFormatted } from '../utils/dateUtils';

const resolveCalendarDateParam = (date) => {
  if (!date) return getTodayFormatted();
  if (typeof date === 'string') return date;
  return formatDateYMD(date);
};

export const buildCalendarByRefForDateUrl = (calendarRefId, date) => {
  const dateParam = resolveCalendarDateParam(date);
  return `${process.env.REACT_APP_API_BASE_URL}/api/calendar/by-ref/${calendarRefId}/for-date?date=${encodeURIComponent(dateParam)}`;
};

export const fetchCalendarByRefForDate = async (calendarRefId, date) => {
  const dateParam = resolveCalendarDateParam(date);
  const response = await httpClient.get(
    `/api/calendar/by-ref/${calendarRefId}/for-date?date=${encodeURIComponent(dateParam)}`
  );
  return response.data;
};

/**
 * Unfiltered calendar lookup — every configured regular-hours/holiday/blackout/
 * recurring event regardless of date, for settings-editing screens. Don't use
 * `fetchCalendarByRefForDate` for this: it silently drops anything that isn't
 * applicable to the specific date queried (defaults to today), so a recurring
 * event that doesn't happen to occur today would never show up.
 */
export const fetchCalendarByRef = async (calendarRefId) => {
  const response = await httpClient.get(`/api/calendar/by-ref/${calendarRefId}`);
  return response.data;
};

// -----------------------------
// LOCATIONS
// -----------------------------

// Fetch shipper locations using office codes (your MT API)
export const fetchLocationsByOfficeCodes = async () => {
  try {
    const response = await httpClient.get(`/api/locations/by-office-codes`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch locations via office codes:", error);
    throw error;
  }
};

// Fetch full location object by locationRef. Pass shipmentNumber/officeCode when the
// caller may not own the location directly (e.g. a carrier viewing a shipper-owned
// pickup location on their own shipment) so the backend can authorize via shipment party.
export const fetchLocationByRef = async (ref, shipmentNumber, officeCode) => {
  try {
    const response = await httpClient.get(`/api/locations/by-loc-ref/${ref}`, {
      params: shipmentNumber ? { shipmentNumber, officeCode } : undefined,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch location by ref:", error);
    throw error;
  }
};

// Fetch warehouses for a location (children locations)
export const fetchWarehousesByLocationId = async (locationId) => {
  try {
    const response = await httpClient.get(
      `/api/locations/children/by-id?parentId=${locationId}`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch warehouses:", error);
    throw error;
  }
};

// -----------------------------
// DOCKS
// -----------------------------

export const fetchDocksForLocationRef = async (locationRef) => {
  try {
    const response = await httpClient.get(`/api/dock/docks/${locationRef}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch docks:", error);
    throw error;
  }
};

export const fetchDockById = async (dockId) => {
  try {
    const response = await httpClient.get(`/api/dock/${dockId}`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch dock by ID:", error);
    throw error;
  }
};

export const updateDock = async (dockId, payload) => {
  try {
    const response = await httpClient.put(
      `/api/dock/${dockId}/update`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to update dock:", error);
    throw error;
  }
};

export const deleteDock = async (dockId) => {
  try {
    await httpClient.delete(`/api/dock/${dockId}`);
  } catch (error) {
    console.error("❌ Failed to delete dock:", error);
    throw error;
  }
};

// -----------------------------
// APPOINTMENTS
// -----------------------------

// Fetch all dock slots for a warehouse (new unified endpoint)
export const fetchWarehouseSlotsAvailability = async (locationRefId, date) => {
  try {
    const response = await httpClient.get(
      `/api/slots/warehouse-availability`,
      {
        params: {
          loc_id_ref: locationRefId,
          date,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch warehouse slot availability:", error);
    throw error;
  }
};

// Fetch appointments for Shipper by locationRef (optionally filtered by date window)
export const fetchAppointmentsByLocationRef = async (locationRef, officeCode, range) => {
  const params =
    range?.start && range?.end
      ? { start: range.start, end: range.end }
      : undefined;

  try {
    const response = await httpClient.get(
      `/api/appointment/by-location`,
      {
        headers: {
          Location: locationRef,
          "Office-Code": officeCode,
        },
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch appointments by location:", error);
    throw error;
  }
};

// Fetch appointment by ID
export const fetchAppointmentById = async (appointmentId, officeCode) => {
  try {
    const response = await httpClient.get(`/api/appointment/${appointmentId}`, {
      headers: { "Office-Code": officeCode },
    });
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch appointment by ID:", error);
    throw error;
  }
};

/** One or more appointments for a shipment (same contract as shipment flow). */
export const fetchAppointmentByShipmentNumber = async (shipmentNumber) => {
  try {
    const response = await httpClient.get(
      `/api/appointment/by-shipment/${encodeURIComponent(shipmentNumber)}`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch appointment by shipment:", error);
    throw error;
  }
};

// Cancel appointment
export const cancelAppointment = async (appointmentId, officeCode) => {
  try {
    await httpClient.patch(`/api/appointment/${appointmentId}/cancel`, null, {
        headers: { "Content-Type": "application/json", "Office-Code": officeCode }
    });
  } catch (error) {
    console.error("❌ Failed to cancel appointment:", error);
    throw error;
  }
};

// Fetch appointments by carrier office code
export const fetchAppointmentsByCarrier = async (officeCodeCarrier, range) => {
  const params =
    range?.start && range?.end
      ? { start: range.start, end: range.end }
      : undefined;

  try {
    const response = await httpClient.get(
      `/api/appointment/by-carrier`,
      {
        headers: {
          "Office-Code-Carrier": officeCodeCarrier,
        },
        params,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch carrier appointments:", error);
    throw error;
  }
};


// -----------------------------
// EVENTS
// -----------------------------
export const fetchEventsByCalendarId = async (calendarId) => {
  const response = await httpClient.get(
    `/api/events/by-calendar-id/${calendarId}`
  );
  return response.data;
};

// Each entry carries its own calendarId, so one call can write to several calendars.
export const createCalendarEvents = async (events) => {
  const response = await httpClient.post(`/api/events/events`, events);
  return response.data;
};

// -----------------------------
// CALENDAR
// -----------------------------

export const fetchEventsByCalendarRefId = async (calendarRefId, date) => {
  return fetchCalendarByRefForDate(calendarRefId, date);
};

export const deleteEvent = async (eventId) => {
  await httpClient.delete(`/api/events/${eventId}`);

};

// -----------------------------
// EXCEPTION EVENTS
// -----------------------------

export const deleteExceptionEvent = async (exceptionId) => {
  await httpClient.delete(`/api/exception-events/${exceptionId}`);
};

export const reopenExceptionEvent = async (exceptionId, payload) => {
  const response = await httpClient.post(
    `/api/exception-events/${exceptionId}/reopen`,
    payload
  );
  return response.data;
};

// -----------------------------
// BULK CLOSURES
// -----------------------------

export const bulkCloseDocks = async (batch, officeCode) => {
  try {
    const response = await httpClient.post(
      `/api/appointments/closures/batch`,
      batch,
      {
        headers: {
          "Office-Code": officeCode,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to perform bulk close:", error);
    throw error;
  }
};

export const rescheduleAppointment = async (appointmentId, payload, officeCode) => {
  try {
    const response = await httpClient.patch(
      `/api/appointment/${appointmentId}/reschedule`,
      payload,
      {
        headers: officeCode ? { "Office-Code": officeCode } : undefined,
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to reschedule appointment:", error);
    throw error;
  }
};

// -----------------------------
// SHIPMENTS
// -----------------------------

export const getShipmentByShipmentNum = async (shipmentNumber, officeCode) => {
  if (!shipmentNumber || !officeCode) {
    console.warn("⚠️ Missing shipmentNumber or officeCode");
    return null;
  }

  try {
    const response = await httpClient.get(
      `/api/shipments/detailed-shipment/${shipmentNumber}`,
      {
        headers: {
          "Office-Code": officeCode
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error(`❌ Failed to fetch shipment ${shipmentNumber}:`, error);
    return null;
  }
};

// -----------------------------
// CALENDAR COPY
// -----------------------------

export const applyWarehouseSettingsToDocks = async (payload) => {
  try {
    const response = await httpClient.post(
      `/api/calendar-setup/apply-to-docks`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to apply warehouse settings to docks:", error);
    throw error;
  }
};
